import { useEffect, useState, type RefObject } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AgentLogo } from 'components/blocks/agent-selector/agent-selector';
import { Popover, PopoverAnchor, PopoverContent } from 'components/ui/popover';
import { useConfig } from 'hooks/use-config';
import { useDebounce } from 'hooks/use-debounce';
import { useGetAgents } from 'features/chat/hooks/use-agents';
import { cn } from 'lib/utils';

const AGENTS_LIMIT = 50;
const SEARCH_DEBOUNCE_MS = 300;

// The API matches the search anywhere in the name ("BuddyAgentByAtik" for "agent"),
// so names that start with what was typed are moved to the top.
function rankByQuery<T extends { name: string }>(agents: T[], search: string): T[] {
  const needle = search.toLowerCase();
  if (!needle) return agents;
  const startsWithQuery = (agent: T) => agent.name.toLowerCase().startsWith(needle);
  return [...agents.filter(startsWithQuery), ...agents.filter((agent) => !startsWithQuery(agent))];
}

type AgentMentionPickerProps = {
  open: boolean;
  query: string;
  onSelect: (agentId: string) => void;
  onClose: () => void;
  inputRef?: RefObject<HTMLElement | null>;
};

/** List of agents shown above the chat input after typing `@`. */
export function AgentMentionPicker({ open, query, onSelect, onClose, inputRef }: AgentMentionPickerProps) {
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();
  const projectKey = configurationSettings?.blocksKey || '';
  const search = useDebounce(query, SEARCH_DEBOUNCE_MS).trim();
  const [focusedIndex, setFocusedIndex] = useState(0);

  const { data, isLoading } = useGetAgents(
    {
      limit: AGENTS_LIMIT,
      offset: 0,
      project_key: projectKey,
      ...(search ? { name: search } : {}),
    },
    { enabled: open && !!projectKey, scope: 'agent-mention' },
  );
  const agents = rankByQuery(
    (data?.agents ?? []).filter((agent) => !agent.is_archived && !agent.is_disabled),
    search,
  );

  useEffect(() => {
    setFocusedIndex(0);
  }, [search, open]);

  useEffect(() => {
    if (!open) return;
    document
      .querySelector(`[data-agent-mention-index="${focusedIndex}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [open, focusedIndex, search]);

  // Arrow keys and Enter are read from the textarea, so focus never leaves the input.
  // Listening on the element stops the event before ChatInput's Enter-to-send runs.
  useEffect(() => {
    const input = inputRef?.current;
    if (!open || !input) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (agents.length === 0) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setFocusedIndex((index) => (index + step + agents.length) % agents.length);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();
        onSelect(agents[Math.min(focusedIndex, agents.length - 1)].id);
      }
    };

    input.addEventListener('keydown', handleKeyDown);
    return () => input.removeEventListener('keydown', handleKeyDown);
  }, [open, inputRef, agents, focusedIndex, onSelect, onClose]);

  let content;
  if (isLoading) {
    content = (
      <div className="flex items-center justify-center gap-2 px-3 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('LOADING', { defaultValue: 'Loading...' })}
      </div>
    );
  } else if (agents.length === 0) {
    content = (
      <p className="px-3 py-6 text-center text-sm text-muted-foreground">
        {t('NO_AGENTS_FOUND', { defaultValue: 'No agents found' })}
      </p>
    );
  } else {
    content = agents.map((agent, index) => (
      <button
        key={agent.id}
        type="button"
        data-agent-mention-index={index}
        className={cn(
          'flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors',
          index === focusedIndex ? 'bg-muted' : 'hover:bg-muted/80',
        )}
        onMouseEnter={() => setFocusedIndex(index)}
        onMouseDown={(event) => {
          event.preventDefault();
          onSelect(agent.id);
        }}
      >
        <AgentLogo agent={agent} className="text-primary dark:text-true-white" />
        <span className="min-w-0 flex-1 truncate font-medium text-foreground" title={agent.name}>
          {agent.name}
        </span>
      </button>
    ));
  }

  // A popover (not an absolute div) so it flips below or shrinks when the input
  // sits too close to the top of the page, instead of being cut off.
  return (
    <Popover open={open} onOpenChange={(isOpen) => !isOpen && onClose()} modal={false}>
      <PopoverAnchor className="pointer-events-none absolute inset-x-0 top-0 h-0" />
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        collisionPadding={12}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          if (inputRef?.current?.contains(event.target as Node)) event.preventDefault();
        }}
        onMouseDown={(event) => event.preventDefault()}
        className="flex max-h-[var(--radix-popover-content-available-height)] w-56 min-w-0 items-stretch overflow-y-auto border-0 bg-transparent p-0 shadow-none dark:border-0 dark:bg-transparent"
      >
        {/* Same card as the `/` categories menu (mention-categories-menu.tsx). */}
        <div className="flex w-full flex-col overflow-hidden rounded-xl border border-border/60 bg-popover shadow-lg">
          <p className="px-3 pb-1 pt-2.5 text-xs text-muted-foreground">
            {t('AGENTS', { defaultValue: 'Agents' })}
          </p>
          <div className="max-h-60 min-h-0 overflow-y-auto py-0.5">{content}</div>
          <p className="border-t border-border/50 px-3 py-2.5 text-xs text-balance text-muted-foreground">
            {t('OR_CONTINUE_TYPING_TO_SEARCH') || 'Or continue typing to search'}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
